import React, { useState } from 'react';
import { Scenario, ScenarioStep } from '../../lib/types';

interface MobileScenarioMapOverlayProps {
  scenario: Scenario;
  step: ScenarioStep;
  stepIndex: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Open the bottom-sheet panel (sets sheetExpanded=true upstream).
      Used as a secondary "read more" affordance when the narrative is
      expanded — drops the user at the top of the panel. */
  onOpenPanel: () => void;
  /** Open the bottom sheet AND auto-scroll the panel content to the
      insight card. Used by the final step's "Open insight ↗" button so
      the user lands on the insight rather than scrolling past mill
      identity / supply-shed toggle / layer dropdown / narrative to find
      it. */
  onOpenInsight: () => void;
}

/**
 * Mobile-only floating card that surfaces the active scenario step's
 * narrative while the user is in map mode (bottom sheet collapsed). On
 * desktop the same narrative lives inside `ScenarioPanel` which is always
 * visible — `md:hidden` keeps this overlay off that path.
 *
 * Default state shows the scenario header + a 3-line clamp of the
 * narrative so the map keeps most of the viewport. Tap the card to
 * expand and read the full narrative inline; tap again to collapse.
 * "Read in panel" jumps the user back into the bottom sheet for full
 * context (auxiliary chart, prev/next controls, insight card on the
 * final step).
 */
export const MobileScenarioMapOverlay: React.FC<MobileScenarioMapOverlayProps> = ({
  scenario,
  step,
  stepIndex,
  totalSteps,
  isFirstStep,
  isLastStep,
  onPrev,
  onNext,
  onOpenPanel,
  onOpenInsight,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      // Top offset clears the MapZoomControl row (44 px control height +
      // 16 px from-top + 8 px gap = 68 px) plus the iOS safe-area inset
      // for notched devices. The control stack itself uses
      // `calc(env(safe-area-inset-top, 0px) + 16px)`, so adding the
      // same inset here keeps the overlay below the controls on every
      // device. Replaces the old `top-[68px]` + `paddingTop: env(...)`
      // pair, which double-counted the inset on some surfaces.
      className="md:hidden absolute left-4 right-4 z-[600] bg-figma-card/95 border border-figma-yellow/50 rounded-lg shadow-figma-card backdrop-blur-sm overflow-hidden"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 68px)' }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-3 py-2.5 flex items-start gap-2 text-left active:bg-figma-panel/50 transition-colors touch-manipulation"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse step narrative' : 'Expand step narrative'}
      >
        <span aria-hidden="true" className="text-base leading-none flex-shrink-0 mt-0.5">
          {scenario.emoji}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-roboto-mono uppercase tracking-[0.10em] text-figma-yellow font-bold">
              {scenario.name}
            </span>
            <span className="text-[10px] font-roboto-mono uppercase tracking-wider text-neutral-400">
              · Step {stepIndex + 1} of {totalSteps}
            </span>
          </div>
          {step.title && (
            <p className="mt-0.5 text-[13px] font-roboto font-bold text-figma-text leading-tight">
              {step.title}
            </p>
          )}
          <p
            className={`mt-1 text-[12px] font-roboto text-neutral-200 leading-snug ${
              expanded ? '' : 'line-clamp-3'
            }`}
          >
            {step.narrative}
          </p>
        </div>
        <span
          aria-hidden="true"
          className={`text-figma-yellow text-base leading-none flex-shrink-0 mt-0.5 transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
        >
          ▾
        </span>
      </button>
      {/* Step controls — always visible so users can advance without
          leaving map mode. On the final step, Next is replaced with
          "Open insight" which expands the bottom sheet to surface the
          insight card (which doesn't fit on the map overlay). */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-figma-yellow/20">
        <button
          type="button"
          onClick={onPrev}
          disabled={isFirstStep}
          className="flex-1 min-h-[36px] px-2 rounded text-[11px] font-roboto-mono uppercase tracking-wider border border-figma-yellow/40 text-figma-yellow disabled:opacity-30 disabled:cursor-not-allowed active:bg-figma-yellow/10 transition-colors"
          aria-label="Previous scenario step"
        >
          ← Prev
        </button>
        {isLastStep ? (
          <button
            type="button"
            onClick={onOpenInsight}
            className="flex-1 min-h-[36px] px-2 rounded text-[11px] font-roboto-mono uppercase tracking-wider bg-figma-yellow text-figma-panel font-bold active:bg-figma-yellow/85 transition-colors"
            aria-label="Open insight in panel"
          >
            Open insight ↗
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            className="flex-1 min-h-[36px] px-2 rounded text-[11px] font-roboto-mono uppercase tracking-wider bg-figma-yellow text-figma-panel font-bold active:bg-figma-yellow/85 transition-colors"
            aria-label="Next scenario step"
          >
            Next →
          </button>
        )}
      </div>
      {expanded && !isLastStep && (
        <div className="px-3 pb-2.5 pt-1 flex justify-end border-t border-figma-yellow/20">
          <button
            type="button"
            onClick={onOpenPanel}
            className="text-[11px] font-roboto-mono uppercase tracking-wider text-figma-yellow active:text-figma-yellow/70 transition-colors py-1 px-2"
          >
            Read in panel ↗
          </button>
        </div>
      )}
    </div>
  );
};
